/**
 * ToastContext — minimal transient-notification system for confirmations like
 * "Copied 6 codes." Renders into a fixed .toast-stack (design-system class) and
 * auto-dismisses. Exposes useToast() → { showToast(message, opts) }.
 *
 * Kept deliberately small: no queueing config, no per-toast actions — this app
 * only needs lightweight confirm/error feedback. If richer behavior is needed
 * later, the design system's full toast anatomy (.toast-title/.toast-message/
 * .toast-close) is already styled and can be adopted here.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Check, AlertCircle, Info } from 'lucide-react';

const ToastContext = createContext(null);

const ICONS = {
  success: Check,
  error: AlertCircle,
  info: Info,
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /**
   * @param {string} message
   * @param {{ variant?: 'success'|'error'|'info', duration?: number }} [opts]
   * @returns {number} the toast's id — pass to dismiss() to clear it early
   *   (e.g. a "this may take a while" toast dismissed once the real work
   *   it's describing actually finishes, rather than on a guessed timer)
   */
  const showToast = useCallback(
    (message, opts = {}) => {
      const id = nextId.current++;
      const variant = opts.variant || 'success';
      const duration = opts.duration ?? 2600;
      setToasts((prev) => [...prev, { id, message, variant }]);
      window.setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={{ showToast, dismiss }}>
      {children}
      <div className="toast-stack" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => {
          const Icon = ICONS[t.variant] || Check;
          return (
            <div key={t.id} className={`toast toast-${t.variant}`} role="status">
              <span className="toast-icon">
                <Icon size={12} />
              </span>
              <div className="toast-body">
                <div className="toast-message">{t.message}</div>
              </div>
              <button
                className="toast-close"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
